import React, { createContext, useContext, useEffect, useState } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signInAnonymously, signOut as fbSignOut, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import {
  doc,
  getFirestore,
  initializeFirestore,
  onSnapshot,
  persistentLocalCache,
  persistentMultipleTabManager,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';

const AuthContext = createContext(null);

// Initialize firebase using same env heuristics as App.jsx
let firebaseConfig = {};
try {
  const raw = (typeof __firebase_config !== 'undefined')
    ? __firebase_config
    : (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_FIREBASE_CONFIG)
      ? import.meta.env.VITE_FIREBASE_CONFIG
      : '{}';
  firebaseConfig = (typeof raw === 'string') ? JSON.parse(raw) : raw;
} catch (e) {
  firebaseConfig = {};
}
const app = Object.keys(firebaseConfig).length > 0 ? initializeApp(firebaseConfig) : null;
const auth = app ? getAuth(app) : null;
const createFirestore = (firebaseApp) => {
  if (!firebaseApp) return null;

  try {
    return initializeFirestore(firebaseApp, {
      localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager(),
      }),
    });
  } catch (error) {
    console.warn('Vision could not enable persistent Firestore cache.', error);
    return getFirestore(firebaseApp);
  }
};
const db = createFirestore(app);
const storage = app ? getStorage(app) : null;
const appId = typeof __app_id !== 'undefined' ? __app_id : 'vision-app';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!auth) {
      setIsLoading(false);
      return;
    }
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (u) {
        try {
          await u.getIdToken(true);
        } catch (error) {
          console.warn('Vision cleared a stale deleted Firebase Auth session.', error);
          try { await fbSignOut(auth); } catch (_) { /* ignore */ }
          setUser(null);
          setUserProfile(null);
          setIsLoading(false);
          return;
        }
      }
      setUser(u || null);
      if (!u) setUserProfile(null);
      setIsLoading(false);
    });

    // Force a token refresh when coming back online so Firestore REST calls
    // don't race against a stale/expired token and hit permission-denied (403).
    const handleOnline = () => {
      const currentUser = auth.currentUser;
      if (currentUser) {
        currentUser.getIdToken(/* forceRefresh= */ true).catch(() => {});
      }
    };
    window.addEventListener('online', handleOnline);

    return () => {
      unsub && unsub();
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  // Load Firestore profile (includes agencyName) whenever user changes
  useEffect(() => {
    if (!db || !user?.uid) { setUserProfile(null); return; }
    const ref = doc(db, 'artifacts', appId, 'users', user.uid);
    const unsub = onSnapshot(ref, (snap) => {
      setUserProfile(snap.exists() ? snap.data() : null);
    }, () => setUserProfile(null));
    return () => unsub();
  }, [user?.uid]);

  const loginAnonymously = async () => {
    if (!auth) throw new Error('Firebase not initialized');
    await signInAnonymously(auth);
  };

  const signUpWithEmail = async (email, password, role = 'recruiter', agencyName = '') => {
    if (!auth) throw new Error('Firebase not initialized');
    const res = await createUserWithEmailAndPassword(auth, email, password);
    const u = res.user;
    try {
      if (db && u && u.uid) {
        // Write only non-server-restricted fields here.
        // 'role', 'accountRole', and agency keys are set server-side
        // by createOwnerAgency / joinAgencyWithSecret Cloud Functions.
        const ref = doc(db, 'artifacts', appId, 'users', u.uid);
        await setDoc(ref, {
          email: u.email || email,
          createdAt: serverTimestamp(),
        }, { merge: true });
      }
    } catch (e) {
      console.error('Failed to write user profile', e);
    }
    return u;
  };

  const signInWithEmail = async (email, password) => {
    if (!auth) throw new Error('Firebase not initialized');
    const res = await signInWithEmailAndPassword(auth, email, password);
    return res.user;
  };

  const resetPassword = async (email) => {
    if (!auth) throw new Error('Firebase not initialized');
    await sendPasswordResetEmail(auth, email);
  };

  const logout = async () => {
    if (!auth) return;
    try { await fbSignOut(auth); } catch (e) { /* ignore */ }
  };

  return (
    <AuthContext.Provider value={{ user, userProfile, isLoading, loginAnonymously, signUpWithEmail, signInWithEmail, resetPassword, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};

export { app, auth, db, storage, appId };

export default AuthContext;


